import { useGoTo } from 'vuetify'
import { sumBy } from 'lodash-es'

import { GENERIC_ERROR_TEXT } from '@/stores/snackbar'
import type { IAddress } from '@/utils/types/core/address'
import { UPLOAD_MAX_SIZE } from '@/utils/constants'

const TIME_TO_DISCOVER_IN_MILLISECONDS = 5 * 1000
const TIME_TO_REDIRECT_IN_MILLISECONDS = 5 * 1000

interface IBenefit {
  // id: number
  // name: string
  image_url: string
  // is_proof_of_purchase_needed: boolean
  // requested_fields: null | string[]
  // key_for_display: null | string
}

interface ICodeDetails {
  has_won: boolean
  has_previously_won: boolean
  code_id: number
  code: string
  proof_of_purchase_needed: null | boolean
  user_information_needed: boolean
  s3_upload_image_path: string
  benefit: null | IBenefit
  validated_at: null | string
  validation_denied_at: null | string
  denial_reason: null | string
  distributed_at: null | string
  success: boolean
}

export const useStoreGame3 = defineStore('game3', () => {
  const nuxtApp = useNuxtApp()
  const router = useRouter()
  const localePath = useLocalePath()
  const goTo = useGoTo()
  const auth = useStoreAuth()
  const snackBar = useStoreSnackbar()

  const isModalOpenProofOfPurchaseNeeded = ref(true)

  const stickerImage = ref<File | null>(null)
  const receipts = ref<File[][]>([])
  const termsAccepted = ref(false)
  const optinToNewsletter = ref(false)

  const hasStickerImage = computed(() => !!stickerImage.value)
  const stickerImageTooBig = computed(() =>
    stickerImage.value ? stickerImage.value.size >= UPLOAD_MAX_SIZE : false,
  )
  const noReceipt = computed(() => receipts.value.length === 0)
  const receiptsContentSize = computed(() =>
    sumBy(receipts.value, (files) => sumBy(files, (file) => file.size)),
  )
  const receiptsAreTooBig = computed(
    () => receiptsContentSize.value >= UPLOAD_MAX_SIZE,
  )
  const canCodeBeValidated = computed(
    () =>
      termsAccepted.value &&
      hasStickerImage.value &&
      !stickerImageTooBig.value &&
      !noReceipt.value &&
      !receiptsAreTooBig.value &&
      true,
  )

  const isBurningCode = ref(false)

  const codeIsLoaded = ref(false)
  const codeDetails = ref<ICodeDetails | null>(null)

  const stickerImageS3Path = ref<string>('')
  const receiptKitUuid = ref<string>('')

  const isResultDiscovered = ref(false)

  const stepValue = ref(1)

  const userTitle = ref('')
  const userLastName = ref('')
  const userFirstName = ref('')
  const userEmail = ref('')
  const userPhoneNumber = ref('')

  const userAddressName = ref('')
  const userAddress1 = ref('')
  const userAddress2 = ref('')
  const userPostalCode = ref('')
  const userCity = ref('')

  const userAddressId = ref<null | number>(null)
  const saveAddressOnProfile = ref(false)

  const isParticipationIncomplete = ref(false)
  const isSubmittingParticipation = ref(false)
  const isParticipationCompleted = ref(false)

  function $reset() {
    // do not reset isModalOpenProofOfPurchaseNeeded here
    // cause it prevents scrolling in playAgain
    // isModalOpenProofOfPurchaseNeeded.value = true

    stickerImage.value = null
    receipts.value = []
    termsAccepted.value = false
    optinToNewsletter.value = false

    isBurningCode.value = false

    codeIsLoaded.value = false
    codeDetails.value = null

    stickerImageS3Path.value = ''
    receiptKitUuid.value = ''

    isResultDiscovered.value = false

    stepValue.value = 1

    userTitle.value = ''
    userLastName.value = ''
    userFirstName.value = ''
    userEmail.value = ''
    userPhoneNumber.value = ''

    userAddressName.value = ''
    userAddress1.value = ''
    userAddress2.value = ''
    userPostalCode.value = ''
    userCity.value = ''

    userAddressId.value = null
    saveAddressOnProfile.value = false

    isParticipationIncomplete.value = false
    isSubmittingParticipation.value = false
    isParticipationCompleted.value = false
  }

  async function playAgain() {
    isModalOpenProofOfPurchaseNeeded.value = false
    await router.push(localePath('game-3'))
    $reset()
    await goTo(0)
    isModalOpenProofOfPurchaseNeeded.value = true
  }

  function setStickerImage($event: Event, handleChange: Function) {
    const input = $event.target as HTMLInputElement
    const newFiles = input.files!
    // if (newFiles.length === 0) return

    stickerImage.value = newFiles[0]
    input.value = ''
    handleChange(stickerImage.value, true)
  }

  function addReceiptWithFiles($event: Event, handleChange: Function) {
    const input = $event.target as HTMLInputElement

    const newFiles = input.files!
    if (newFiles.length === 0) return

    const filesToAdd: File[] = []
    for (const file of newFiles) {
      filesToAdd.push(file)
    }
    receipts.value.push(filesToAdd)

    input.value = ''

    handleChange(receipts.value, true)
  }
  function addFilesToReceipt(
    {
      receiptIndex,
      files,
    }: {
      receiptIndex: number
      files: File[]
    },
    handleChange: Function,
  ) {
    receipts.value[receiptIndex].push(...files)
    handleChange(receipts.value, true)
  }
  function removeFileFromReceipt(
    {
      receiptIndex,
      fileIndex,
    }: {
      receiptIndex: number
      fileIndex: number
    },
    handleChange: Function,
  ) {
    receipts.value[receiptIndex].splice(fileIndex, 1)

    if (receipts.value[receiptIndex].length === 0) {
      receipts.value.splice(receiptIndex, 1)
    }

    handleChange(receipts.value, true)
  }

  async function burnCode() {
    if (isBurningCode.value) return

    isBurningCode.value = true

    let response
    try {
      response = await nuxtApp.$PE.client.burnUniqCode({ code: 'N/A' })
    } catch (error) {
      nuxtApp.$airbrakeNotify({
        error,
        context: { locator: 'burnCode #1' },
      })
      snackBar.addToastWithGenericError()
      isBurningCode.value = false
      return
    }

    if (response.error) {
      let text
      switch (response.error.user) {
        case 'has_already_participated':
          text =
            "Vous avez déjà participité aujourd'hui, vous pourrez retenter votre chance demain avec une autre preuve d'achat."
          break
        case 'locked':
          text =
            'Trop de demandes simultanées. Merci de retenter dans un instant.'
          break
        case 'winner_locked':
          text =
            'Une autre requête est déjà en cours de traitement. Merci de retenter dans un instant.'
          break
        default:
          nuxtApp.$airbrakeNotify({
            error: new Error('unhandled error code from backend'),
            params: { response },
            context: { locator: 'burnCode #2' },
          })
          text = GENERIC_ERROR_TEXT
      }
      snackBar.addToast({ text, timeout: 8000 })
      isBurningCode.value = false
      return
    }

    codeDetails.value = response
    codeIsLoaded.value = true

    if (codeDetails.value)
      return submitFilesAndLinkThemToCode()
        .then(
          () => {
            if (codeDetails.value?.has_previously_won) {
              isParticipationIncomplete.value = true
              router.push(localePath('game-3-winner'))
            } else {
              router.push(localePath('game-3-result'))
            }
          },
          (error) => {
            snackBar.addToastWithGenericError()
            nuxtApp.$airbrakeNotify({
              error,
              context: { locator: 'burnCode #3' },
            })
          },
        )
        .finally(() => {
          isBurningCode.value = false
        })
  }

  async function submitFilesAndLinkThemToCode() {
    return Promise.all([uploadStickerImage(), submitReceipt()]).then(
      linksFilesToCode,
    )
  }

  async function uploadStickerImage() {
    const file = stickerImage.value
    if (!file) {
      throw new Error('no file')
    }
    if (!codeDetails.value) {
      throw new Error('no code details')
    }

    const nameParts = file.name.split('.')
    const extension = nameParts[nameParts.length - 1]
    stickerImageS3Path.value = `${codeDetails.value.s3_upload_image_path}.${extension}`
    const url = `https://purchease-com-user-captures.s3.eu-west-1.amazonaws.com/${stickerImageS3Path.value}`
    const aclHeader = 'bucket-owner-full-control'
    const contentTypeHeader = file.type

    const headers = {
      'x-amz-acl': aclHeader,
      'Content-Type': contentTypeHeader,
    }

    const response = await fetch(url, {
      method: 'PUT',
      headers: headers,
      body: file,
    })

    if (!response.ok) {
      throw new Error('failed to upload image')
    }
  }

  async function submitReceipt() {
    let response
    try {
      response = await nuxtApp.$PE.client.uploadWithFiles({
        files: receipts.value[0],
        customFields: { optin_to_newsletter: optinToNewsletter.value },
      })
    } catch (error) {
      nuxtApp.$airbrakeNotify({
        error,
        context: {
          locator: 'submitReceipt -> PE.client.uploadWithFiles',
        },
      })
      throw error
    }

    receiptKitUuid.value = response
  }

  async function linksFilesToCode() {
    if (!codeDetails.value) {
      throw new Error('no code details')
    }

    let response
    try {
      response = await nuxtApp.$PE.client.linkCodeToReceipt({
        codeId: codeDetails.value.code_id,
        receiptKitUuid: receiptKitUuid.value,
        productShotImageKey: stickerImageS3Path.value,
      })
    } catch (error) {
      nuxtApp.$airbrakeNotify({
        error,
        context: {
          locator: 'linksFilesToCode -> PE.client.linkCodeToReceipt',
        },
      })
      throw error
    }

    if (!response.success) {
      const error = new Error(
        'badrequest for linksFilesToCode -> PE.client.linkCodeToReceipt',
      )
      nuxtApp.$airbrakeNotify({
        error,
        params: { response },
        context: {
          locator: 'linksFilesToCode -> PE.client.linkCodeToReceipt',
        },
      })
      throw error
    }
  }

  function makeSureCodeIsLoadedOrRestart() {
    if (codeIsLoaded.value) return

    playAgain()
  }

  function discoverResult() {
    if (isResultDiscovered.value) return

    isResultDiscovered.value = true

    setTimeout(async () => {
      await router.push(
        localePath(
          codeDetails.value?.has_won ? 'game-3-winner' : 'game-3-loser',
        ),
      )
    }, TIME_TO_REDIRECT_IN_MILLISECONDS)
  }
  function discoverResultAfterSomeTime() {
    setTimeout(discoverResult, TIME_TO_DISCOVER_IN_MILLISECONDS)
  }

  function updateUserInfoFromAuth() {
    userTitle.value = auth.user?.title ? auth.user.title : ''
    userLastName.value = auth.user?.lastName ? auth.user.lastName : ''
    userFirstName.value = auth.user?.firstName ? auth.user.firstName : ''
    userEmail.value = auth.user?.email ? auth.user.email : ''
    userPhoneNumber.value = auth.user?.phone ? auth.user.phone : ''
  }
  function updateAddressDetails(address: IAddress) {
    userAddressName.value = address.user_defined_identifier
    userAddress1.value = address.address_1
    userAddress2.value = address.address_2
    userCity.value = address.city
    userPostalCode.value = address.postal_code
  }

  function goToStep1() {
    stepValue.value = 1
    isParticipationIncomplete.value = true
  }
  function goToStep2() {
    stepValue.value = 2
  }

  async function submitParticipation() {
    if (!codeDetails.value) {
      throw new Error('no code details')
    }
    if (isSubmittingParticipation.value) return

    isSubmittingParticipation.value = true

    let response
    try {
      response = await nuxtApp.$PE.client.linkCodeToUserInformation({
        codeId: codeDetails.value.code_id,
        userInformationFields: {
          user_defined_identifier: saveAddressOnProfile.value
            ? userAddressName.value
            : null,
          title: userTitle.value,
          last_name: userLastName.value,
          first_name: userFirstName.value,
          email: userEmail.value,
          phone_number: userPhoneNumber.value,
          address_1: userAddress1.value,
          address_2: userAddress2.value,
          postal_code: userPostalCode.value,
          city: userCity.value,
        },
      })
    } catch (error) {
      isSubmittingParticipation.value = false
      snackBar.addToastWithGenericError()
      nuxtApp.$airbrakeNotify({
        error,
        context: {
          locator: 'submitParticipation -> PE.client.linkCodeToUserInformation',
        },
      })
      return
    }

    if (!response.success) {
      isSubmittingParticipation.value = false
      snackBar.addToastWithGenericError()
      nuxtApp.$airbrakeNotify({
        error: new Error('bad request'),
        params: { response },
        context: {
          locator: 'submitParticipation -> PE.client.linkCodeToUserInformation',
        },
      })
      return
    }

    isSubmittingParticipation.value = false
    isParticipationCompleted.value = true
  }

  return {
    $reset,

    playAgain,

    isModalOpenProofOfPurchaseNeeded,

    stickerImage,
    setStickerImage,
    receipts,
    termsAccepted,
    optinToNewsletter,

    noReceipt,
    stickerImageTooBig,
    receiptsAreTooBig,
    canCodeBeValidated,

    addReceiptWithFiles,
    addFilesToReceipt,
    removeFileFromReceipt,

    isBurningCode,
    burnCode,

    codeDetails,

    isResultDiscovered,
    discoverResult,
    discoverResultAfterSomeTime,

    makeSureCodeIsLoadedOrRestart,

    updateUserInfoFromAuth,

    stepValue,

    userTitle,
    userLastName,
    userFirstName,
    userEmail,
    userPhoneNumber,

    userAddressName,
    userAddress1,
    userAddress2,
    userPostalCode,
    userCity,

    userAddressId,
    saveAddressOnProfile,
    updateAddressDetails,

    goToStep1,
    goToStep2,

    isParticipationIncomplete,

    isSubmittingParticipation,
    submitParticipation,
    isParticipationCompleted,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useStoreGame3, import.meta.hot))
}
